import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { ordersAPI } from '../services/api';
import { formatCurrency } from '../utils/format';
import { customToast, toastMessages } from '../utils/toast';
import { useLanguage } from '../contexts/LanguageContext';
import {
  Clock,
  CheckCircle,
  XCircle,
  CreditCard,
  Eye,
  ShoppingCart,
  Sparkles,
  Search,
  RefreshCw,
  Package,
  AlertCircle,
  DollarSign
} from 'lucide-react';

const StaffOrderManagement = () => {
  const { t } = useLanguage();
  const [selectedStatus, setSelectedStatus] = useState('active');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const queryClient = useQueryClient();

  // Fetch orders with auto-refresh
  const { data: ordersData, isLoading, refetch } = useQuery(
    'orders',
    () => ordersAPI.getOrders(),
    {
      refetchInterval: 10000, // Refresh every 10 seconds for staff
      onError: (error) => {
        console.error('Error fetching orders:', error);
        customToast.error(toastMessages.orderLoadError);
      }
    }
  );


  // Transform API data
  const transformedOrders = React.useMemo(() => {
    if (!ordersData) return [];

    const data = ordersData.data || ordersData;
    if (!Array.isArray(data)) return [];

    return data.map((order, index) => ({
      id: order.id || order.order_id || order.orderId || `ORD-${Date.now()}-${index}`,
      table_number: order.table_number || order.table_id || order.tableNumber || 'N/A',
      status: order.status || 'pending',
      payment_status: order.payment_status || order.paymentStatus || 'unpaid',
      total_amount: order.total_amount || order.total || order.totalAmount || 0,
      created_at: order.created_at || order.order_date || order.createdAt || new Date().toISOString(),
      items: order.items || order.order_items || [],
      customizations: order.customizations || order.special_instructions || '',
      estimated_time: order.estimated_time || order.eta
    }));
  }, [ordersData]);

  // Staff-specific status update mutation
  const updateStatusMutation = useMutation(
    async (data) => {
      const response = await ordersAPI.updateOrderStatus(data);
      return response;
    },
    {
      onMutate: async (data) => {
        await queryClient.cancelQueries('orders');
        const previousOrders = queryClient.getQueryData('orders');

        queryClient.setQueryData('orders', (old) => {
          if (!old) return old;
          const dataToUpdate = old.data || old;
          if (!Array.isArray(dataToUpdate)) return old;

          return {
            ...old,
            data: dataToUpdate.map(order =>
              (order.id === data.id || order.order_id === data.id)
                ? { ...order, status: data.status }
                : order
            )
          };
        });

        return { previousOrders };
      },
      onError: (error, variables, context) => {
        if (context?.previousOrders) {
          queryClient.setQueryData('orders', context.previousOrders);
        }
        customToast.error(toastMessages.updateError('order status'));
      },
      onSuccess: () => {
        queryClient.invalidateQueries('orders');
      },
    }
  );

  // Payment update mutation for staff
  const updatePaymentMutation = useMutation(
    (data) => ordersAPI.updatePaymentStatus(data),
    {
      onMutate: async (data) => {
        await queryClient.cancelQueries('orders');
        const previousOrders = queryClient.getQueryData('orders');

        queryClient.setQueryData('orders', (old) => {
          if (!old) return old;

          const dataToUpdate = old.data || old;
          if (!Array.isArray(dataToUpdate)) return old;

          return {
            ...old,
            data: dataToUpdate.map(order =>
              (order.id === data.id || order.order_id === data.id)
                ? {
                  ...order,
                  payment_status: data.payment_status,
                  payment_method: data.payment_method
                }
                : order
            )
          };
        });

        return { previousOrders };
      },
      onError: (error, variables, context) => {
        if (context?.previousOrders) {
          queryClient.setQueryData('orders', context.previousOrders);
        }
        console.error('Update payment error:', error);
        customToast.error(toastMessages.updateError('payment status'));
      },
      onSuccess: () => {
        queryClient.invalidateQueries('orders');
        customToast.success(toastMessages.paymentUpdated);
      },
    }
  );

  // Staff can update order status and payment
  const handleStatusUpdate = async (orderId, status) => {
    try {
      await updateStatusMutation.mutateAsync({
        id: orderId,
        status: status
      });

      if (selectedOrder) {
        setSelectedOrder(null);
      }
    } catch (error) {
      console.error('Status update failed:', error);
    }
  };

  const handlePaymentUpdate = async (orderId, paymentStatus, paymentMethod) => {
    try {
      await updatePaymentMutation.mutateAsync({
        id: orderId,
        payment_status: paymentStatus,
        payment_method: paymentMethod
      });

      if (selectedOrder) {
        setSelectedOrder(null);
      }
    } catch (error) {
      console.error('Payment update failed:', error);
    }
  };

  // Filter orders for staff - only show active orders by default
  const filteredOrders = transformedOrders.filter(order => {
    const statusMatch = selectedStatus === 'active'
      ? (order.status === 'pending' || order.status === 'in_progress')
      : order.status === selectedStatus;

    const searchMatch = order.id.toString().includes(searchTerm) ||
      order.table_number.toString().includes(searchTerm);

    return statusMatch && searchMatch;
  });

  // Get urgency level for orders
  const getUrgencyLevel = (order) => {
    if (order.status !== 'pending' && order.status !== 'in_progress') {
      return 'none';
    }

    const orderTime = new Date(order.created_at);
    const now = new Date();
    const minutesPassed = (now - orderTime) / (1000 * 60);

    if (minutesPassed > 30) return 'high';
    if (minutesPassed > 15) return 'medium';
    return 'low';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'text-yellow-600 bg-yellow-100 border-yellow-200';
      case 'in_progress': return 'text-blue-600 bg-blue-100 border-blue-200';
      case 'completed': return 'text-green-600 bg-green-100 border-green-200';
      case 'cancelled': return 'text-red-600 bg-red-100 border-red-200';
      default: return 'text-gray-600 bg-gray-100 border-gray-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return <Clock className="h-4 w-4" />;
      case 'in_progress': return <RefreshCw className="h-4 w-4" />;
      case 'completed': return <CheckCircle className="h-4 w-4" />;
      case 'cancelled': return <XCircle className="h-4 w-4" />;
      default: return <Clock className="h-4 w-4" />;
    }
  };

  const getPaymentStatusColor = (status) => {
    switch (status) {
      case 'paid': return 'text-green-600 bg-green-100 border-green-200';
      case 'unpaid': return 'text-red-600 bg-red-100 border-red-200';
      default: return 'text-gray-600 bg-gray-100 border-gray-200';
    }
  };

  const getStatusTranslationKey = (status) => {
    const statusMap = {
      'pending': 'pending',
      'in_progress': 'inProgress',
      'completed': 'completed',
      'cancelled': 'cancelled'
    };
    return `orders.status.${statusMap[status] || status}`;
  };

  // Staff-specific stats
  const pendingOrders = transformedOrders.filter(o => o.status === 'pending').length;
  const inProgressOrders = transformedOrders.filter(o => o.status === 'in_progress').length;
  const unpaidOrders = transformedOrders.filter(o => o.payment_status === 'unpaid' && o.status !== 'cancelled').length;

  const stats = [
    {
      icon: ShoppingCart,
      label: t('dashboard.stats.totalOrders'),
      value: transformedOrders.length,
      color: 'blue',
      description: t('dashboard.stats.ordersToday')
    },
    {
      icon: Clock,
      label: t('orders.status.pending'),
      value: pendingOrders,
      color: 'yellow',
      description: t('dashboard.stats.awaitingPreparation')
    },
    {
      icon: RefreshCw,
      label: t('orders.status.inProgress'),
      value: inProgressOrders,
      color: 'blue',
      description: t('dashboard.stats.beingPrepared')
    },
    {
      icon: DollarSign,
      label: t('orders.paymentStatus.unpaid'),
      value: unpaidOrders,
      change: 'Needs attention',
      color: 'red',
      description: t('dashboard.stats.unpaidDescription', 'Awaiting payment')
    }
  ];

  // Staff-specific status filters
  const statusFilters = [
    { key: 'active', label: t('orders.active', "Active"), count: pendingOrders + inProgressOrders },
    { key: 'pending', label: t('orders.status.pending'), count: pendingOrders },
    { key: 'in_progress', label: t('orders.status.inProgress'), count: inProgressOrders },
    { key: 'completed', label: t('orders.status.completed'), count: transformedOrders.filter(o => o.status === 'completed').length },
    { key: 'unpaid', label: t('orders.paymentStatus.unpaid'), count: unpaidOrders },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50/30 p-5">
      {/* Animated Background */}
      <div className="absolute inset-0 -z-10 h-full w-full bg-white bg-[linear-gradient(to_right,#f0f0f0_1px,transparent_1px),linear-gradient(to_bottom,#f0f0f0_1px,transparent_1px)] bg-[size:6rem_4rem]">
        <div className="absolute bottom-0 left-0 right-0 top-0 bg-[radial-gradient(circle_800px_at_50%_100px,#f0f9ff,transparent)]"></div>
      </div>

      <div className="container mx-auto px-4 py-6 md:py-8">
        {/* Header */}
        <div className={`mb-6 md:mb-8 transition-all duration-700 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
          }`}>
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <div className="inline-flex items-center gap-2 bg-gradient-to-r from-green-50 to-emerald-50 text-green-700 px-3 py-1 rounded-full text-sm font-medium">
                  <Sparkles className="h-4 w-4" />
                  {t('orders.staffTitle', 'Staff Orders')}
                </div>
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              </div>
              <h1 className="text-2xl md:text-4xl font-bold text-gray-900 mb-2 md:mb-3">
                {t('orders.title', 'Order Management')}
              </h1>
              <p className="text-gray-600 text-sm md:text-lg max-w-2xl">
                {t('orders.subtitle', 'Process orders and handle payments efficiently')}
              </p>
            </div>
          </div>
        </div>

        {/* Stats Overview */}
        <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-6 md:mb-8 transition-all duration-700 delay-200 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
          }`}>
          {stats.map((stat, index) => {
            const Icon = stat.icon;
            const colorClasses = {
              blue: 'from-blue-500 to-cyan-500',
              yellow: 'from-yellow-500 to-amber-500',
              red: 'from-red-500 to-pink-500',
              green: 'from-green-500 to-emerald-500'
            };
            return (
              <div
                key={index}
                className="group bg-white rounded-2xl p-4 md:p-6 shadow-lg hover:shadow-xl transition-all duration-500 hover:-translate-y-1 md:hover:-translate-y-2 border border-gray-100"
              >
                <div className="flex items-center justify-between mb-3 md:mb-4">
                  <div className={`p-2 md:p-3 rounded-xl bg-gradient-to-r ${colorClasses[stat.color]} text-white group-hover:scale-110 transition-transform duration-300`}>
                    <Icon className="h-5 w-5 md:h-6 md:w-6" />
                  </div>
                </div>

                <div>
                  <p className="text-xl md:text-2xl lg:text-3xl font-bold text-gray-900 mb-1">{stat.value}</p>
                  <p className="text-xs md:text-sm font-medium text-gray-600">{stat.label}</p>
                  <p className="text-xs text-gray-500 mt-1">{stat.description}</p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Filters and Actions */}
        <div className={`bg-white rounded-2xl shadow-lg border border-gray-100 p-4 md:p-6 mb-6 md:mb-8 transition-all duration-700 delay-300 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
          }`}>
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            {/* Status Filter */}
            <div className="flex flex-wrap gap-2">
              {statusFilters.map((status) => (
                <button
                  key={status.key}
                  onClick={() => setSelectedStatus(status.key)}
                  className={`px-3 py-1.5 md:px-4 md:py-2 rounded-xl text-xs md:text-sm font-medium transition-all duration-300 whitespace-nowrap ${selectedStatus === status.key
                    ? 'bg-blue-600 text-white shadow-lg'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200'
                    }`}
                >
                  {status.label}
                  <span className={`ml-1 md:ml-2 px-1 md:px-1.5 py-0.5 rounded-full text-xs ${selectedStatus === status.key ? 'bg-white/20' : 'bg-gray-200'
                    }`}>
                    {status.count}
                  </span>
                </button>
              ))}
            </div>

            {/* Search and Actions */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              {/* Search */}
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder={t('common.search.placeholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                />
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => refetch()}
                  className="flex items-center gap-2 bg-blue-600 text-white px-3 md:px-4 py-2 rounded-xl hover:bg-blue-700 transition-all duration-300 hover:scale-105"
                >
                  <RefreshCw className="h-4 w-4" />
                  <span className="text-sm font-medium">{t('common.refresh')}</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Orders List */}
        {isLoading ? (
          <div className="flex justify-center py-8 md:py-12">
            <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : filteredOrders.length === 0 ? (
          <div className="text-center py-12 md:py-16 bg-white rounded-2xl shadow-lg border border-gray-100">
            <div className="w-16 h-16 md:w-20 md:h-20 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-2xl flex items-center justify-center mx-auto mb-4 md:mb-6">
              <ShoppingCart className="h-8 w-8 md:h-10 md:w-10 text-blue-600" />
            </div>
            <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-2 md:mb-3">
              {t('orders.empty.title')}
            </h3>
            <p className="text-gray-600 mb-6 md:mb-8 max-w-md mx-auto text-sm md:text-base">
              {searchTerm || selectedStatus !== 'active'
                ? t('orders.empty.filtered')
                : t('orders.empty.default')
              }
            </p>
          </div>
        ) : (
          <div className={`space-y-3 md:space-y-4 transition-all duration-700 delay-400 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`}>
            {filteredOrders.map((order) => {
              const urgency = getUrgencyLevel(order);
              const isActiveOrder = order.status === 'pending' || order.status === 'in_progress';
              const waitingTime = Math.round((new Date() - new Date(order.created_at)) / (1000 * 60));

              return (
                <div
                  key={order.id}
                  className={`group bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-500 hover:-translate-y-1 md:hover:-translate-y-2 border ${isActiveOrder && urgency === 'high' ? 'border-red-200 bg-red-50' :
                    isActiveOrder && urgency === 'medium' ? 'border-orange-200 bg-orange-50' :
                      order.status === 'completed' ? 'border-green-200 bg-green-50' :
                        'border-gray-200'
                    }`}
                >
                  <div className="p-4 md:p-6">
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                      <div className="flex items-start gap-3 md:gap-4 flex-1 min-w-0">
                        <div className={`p-2 md:p-3 rounded-xl border ${getStatusColor(order.status)} flex-shrink-0`}>
                          {getStatusIcon(order.status)}
                        </div>

                        <div className="flex-1 min-w-0">
                          <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-2">
                            <h3 className="text-lg md:text-xl font-bold text-gray-900 truncate">
                              {t('orders.orderNumber')} #{order.id}
                            </h3>
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(order.status)} whitespace-nowrap`}>
                                {t(getStatusTranslationKey(order.status))}
                              </span>
                              <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getPaymentStatusColor(order.payment_status)} whitespace-nowrap`}>
                                {t(`orders.paymentStatus.${order.payment_status}`)}
                              </span>
                              {isActiveOrder && urgency === 'high' && (
                                <span className="px-2 py-1 bg-red-100 text-red-800 rounded-full text-xs font-medium animate-pulse whitespace-nowrap">
                                  {t('orders.urgency.high', 'URGENT')}
                                </span>
                              )}
                              {isActiveOrder && urgency === 'medium' && (
                                <span className="px-2 py-1 bg-orange-100 text-orange-800 rounded-full text-xs font-medium whitespace-nowrap">
                                  {t('orders.urgency.medium', 'Waiting')}
                                </span>
                              )}
                            </div>
                          </div>

                          <p className="text-xs md:text-sm text-gray-600 truncate">
                            {t('dashboard.recentOrders.tableInfo', 'Table {table} • {amount} • {items} items')
                              .replace('{table}', order.table_number)
                              .replace('{amount}', formatCurrency(order.total_amount))
                              .replace('{items}', order.items?.length || 0)
                            }
                          </p>

                          <div className="flex items-center gap-3 md:gap-4 text-xs md:text-sm text-gray-500 flex-wrap mt-2">
                            <span className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {waitingTime}min {t('orders.waiting', 'waiting')}
                            </span>
                            {order.estimated_time && (
                              <span className="flex items-center gap-1 text-blue-600 font-medium">
                                <AlertCircle className="h-3 w-3" />
                                ETA: {order.estimated_time}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 md:gap-3 flex-wrap">
                        <button
                          onClick={() => setSelectedOrder(order)}
                          className="bg-blue-600 text-white px-3 py-1.5 md:px-4 md:py-2 rounded-xl font-medium hover:bg-blue-700 transition-all duration-300 hover:scale-105 flex items-center gap-1 md:gap-2 text-sm"
                        >
                          <Eye className="h-3 w-3 md:h-4 md:w-4" />
                          <span className="hidden xs:inline">{t('common.view')}</span>
                        </button>

                        {order.status === 'pending' && (
                          <button
                            onClick={() => handleStatusUpdate(order.id, 'in_progress')}
                            className="bg-yellow-600 text-white px-3 py-1.5 md:px-4 md:py-2 rounded-xl font-medium hover:bg-yellow-700 transition-all duration-300 hover:scale-105 text-sm whitespace-nowrap"
                            disabled={updateStatusMutation.isLoading}
                          >
                            {t('orders.actions.start')}
                          </button>
                        )}

                        {order.status === 'in_progress' && (
                          <button
                            onClick={() => handleStatusUpdate(order.id, 'completed')}
                            className="bg-green-600 text-white px-3 py-1.5 md:px-4 md:py-2 rounded-xl font-medium hover:bg-green-700 transition-all duration-300 hover:scale-105 text-sm whitespace-nowrap"
                            disabled={updateStatusMutation.isLoading}
                          >
                            {t('orders.actions.complete')}
                          </button>
                        )}

                        {order.payment_status === 'unpaid' && order.status !== 'cancelled' && (
                          <button
                            onClick={() => handlePaymentUpdate(order.id, 'paid', 'cash')}
                            className="bg-green-600 text-white px-3 py-1.5 md:px-4 md:py-2 rounded-xl font-medium hover:bg-green-700 transition-all duration-300 hover:scale-105 flex items-center gap-1 md:gap-2 text-sm whitespace-nowrap"
                            disabled={updatePaymentMutation.isLoading}
                          >
                            <CreditCard className="h-3 w-3 md:h-4 md:w-4" />
                            <span className="hidden xs:inline">{t('orders.actions.pay')}</span>
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Order Details Modal */}
        {selectedOrder && (
          <StaffOrderDetailsModal
            order={selectedOrder}
            onClose={() => setSelectedOrder(null)}
            onStatusUpdate={handleStatusUpdate}
            onPaymentUpdate={handlePaymentUpdate}
            isLoading={updateStatusMutation.isLoading || updatePaymentMutation.isLoading}
            isUpdatingStatus={updateStatusMutation.isLoading}
            isUpdatingPayment={updatePaymentMutation.isLoading}
          />
        )}
      </div>
    </div>
  );
};

// Staff-specific Order Details Modal with Payment
const StaffOrderDetailsModal = ({ order, onClose, onStatusUpdate, onPaymentUpdate, isLoading, isUpdatingStatus, isUpdatingPayment }) => {
  const { t } = useLanguage();
  const [orderItems, setOrderItems] = useState([]);
  const [loadingItems, setLoadingItems] = useState(false);

  useEffect(() => {
    if (order) {
      setLoadingItems(true);
      ordersAPI.getOrderById(order.id)
        .then(response => {
          const orderData = response.data || response;
          setOrderItems(orderData.items || orderData.order_items || []);
        })
        .catch(error => {
          console.error('Failed to fetch order items:', error);
          customToast.error(toastMessages.updateError('order details'));
        })
        .finally(() => {
          setLoadingItems(false);
        });
    }
  }, [order]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'text-yellow-600 bg-yellow-100 border-yellow-200';
      case 'in_progress': return 'text-blue-600 bg-blue-100 border-blue-200';
      case 'completed': return 'text-green-600 bg-green-100 border-green-200';
      case 'cancelled': return 'text-red-600 bg-red-100 border-red-200';
      default: return 'text-gray-600 bg-gray-100 border-gray-200';
    }
  };

  const getPaymentStatusColor = (status) => {
    switch (status) {
      case 'paid': return 'text-green-600 bg-green-100 border-green-200';
      case 'unpaid': return 'text-red-600 bg-red-100 border-red-200';
      default: return 'text-gray-600 bg-gray-100 border-gray-200';
    }
  };

  const getStatusTranslationKey = (status) => {
    const statusMap = {
      'pending': 'pending',
      'in_progress': 'inProgress',
      'completed': 'completed',
      'cancelled': 'cancelled'
    };
    return `orders.status.${statusMap[status] || status}`;
  };

  const totalItems = orderItems.reduce((sum, item) => sum + (item.quantity || 0), 0);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl p-4 md:p-6 lg:p-8 w-full max-w-4xl max-h-screen overflow-y-auto shadow-2xl border border-gray-100">
        {/* Header */}
        <div className="flex items-center justify-between mb-4 md:mb-6">
          <div>
            <h3 className="text-xl md:text-2xl font-bold text-gray-900">
              {t('orders.details.title', { number: order.id })}
            </h3>
            <p className="text-gray-600 mt-1 text-sm md:text-base">{t('orders.subtitle')}</p>
          </div>
          <button
            onClick={onClose}
            className="p-1 md:p-2 hover:bg-gray-100 rounded-xl transition-colors duration-300"
          >
            <XCircle className="h-5 w-5 md:h-6 md:w-6 text-gray-400" />
          </button>
        </div>

        {/* Order Overview */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-6 md:mb-8">
          <div className="bg-gray-50 rounded-xl p-3 md:p-4 border border-gray-200">
            <p className="text-xs md:text-sm text-gray-600 mb-1">{t('orders.details.tableNumber')}</p>
            <p className="font-bold text-gray-900 text-base md:text-lg">Table {order.table_number}</p>
          </div>
          <div className="bg-gray-50 rounded-xl p-3 md:p-4 border border-gray-200">
            <p className="text-xs md:text-sm text-gray-600 mb-1">{t('orders.details.orderTime')}</p>
            <p className="font-bold text-gray-900 text-base md:text-lg">{new Date(order.created_at).toLocaleTimeString()}</p>
            <p className="text-xs text-gray-500">{new Date(order.created_at).toLocaleDateString()}</p>
          </div>
          <div className="bg-gray-50 rounded-xl p-3 md:p-4 border border-gray-200">
            <p className="text-xs md:text-sm text-gray-600 mb-1">{t('orders.details.status')}</p>
            <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(order.status)} whitespace-nowrap`}>
              {t(getStatusTranslationKey(order.status))}
            </span>
          </div>
          <div className="bg-gray-50 rounded-xl p-3 md:p-4 border border-gray-200">
            <p className="text-xs md:text-sm text-gray-600 mb-1">{t('orders.details.payment')}</p>
            <span className={`inline-flex items-center px-2 py-1 md:px-3 md:py-1 rounded-full text-xs md:text-sm font-medium border ${getPaymentStatusColor(order.payment_status)}`}>
              {t(`orders.paymentStatus.${order.payment_status}`)}
            </span>
          </div>
        </div>

        {/* Order Items with Customizations */}
        <div className="mb-6 md:mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-3 md:mb-4 gap-2">
            <h4 className="text-lg md:text-xl font-bold text-gray-900">
              {t('orders.details.itemsTitle')} : {totalItems}
            </h4>
          </div>

          {loadingItems ? (
            <div className="flex justify-center py-6 md:py-8">
              <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : orderItems.length === 0 ? (
            <div className="text-center py-6 md:py-8 bg-gray-50 rounded-xl border border-gray-200">
              <Package className="h-8 w-8 md:h-12 md:w-12 mx-auto mb-2 md:mb-3 text-gray-400" />
              <p className="text-gray-500 text-sm md:text-base">{t('orders.details.noItems')}</p>
            </div>
          ) : (
            <div className="space-y-3 md:space-y-4">
              {orderItems.map((item) => (
                <div key={item.id} className="bg-gray-50 rounded-xl border border-gray-200 hover:bg-gray-100 transition-colors duration-300 overflow-hidden">
                  <div className="p-3 md:p-4">
                    <div className="flex flex-col sm:flex-row sm:items-start justify-between mb-2 md:mb-3 gap-2">
                      <div className="flex-1">
                        <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-2">
                          <h5 className="font-semibold text-gray-900 text-base md:text-lg">{item.dish_name || item.name}</h5>
                          <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded-full text-xs md:text-sm font-medium self-start sm:self-center">
                            {t('orders.details.quantity')} {item.quantity}
                          </span>
                        </div>

                        {/* Customizations Display */}
                        {item.customizations && item.customizations.trim() !== '' && (
                          <div className="mt-2 p-2 md:p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                            <div className="flex items-start gap-2">
                              <AlertCircle className="h-3 w-3 md:h-4 md:w-4 text-yellow-600 mt-0.5 flex-shrink-0" />
                              <div>
                                <p className="text-xs md:text-sm font-medium text-yellow-800 mb-1">{t('orders.details.specialInstructions')}</p>
                                <p className="text-yellow-700 text-xs md:text-sm">{item.customizations}</p>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Order-level Customizations */}
        {order.customizations && order.customizations.trim() !== '' && (
          <div className="mb-4 md:mb-6 p-3 md:p-4 bg-blue-50 border border-blue-200 rounded-xl">
            <div className="flex items-start gap-2 md:gap-3">
              <AlertCircle className="h-4 w-4 md:h-5 md:w-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium text-blue-800 mb-1 text-sm md:text-base">{t('orders.details.orderInstructions')}</p>
                <p className="text-blue-700 text-sm md:text-base">{order.customizations}</p>
              </div>
            </div>
          </div>
        )}

        {/* Total and Actions */}
        <div className="border-t pt-4 md:pt-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 md:mb-6 gap-2">
            <div>
              <p className="text-base md:text-lg font-semibold text-gray-900">{t('orders.details.orderTotal')}</p>
              <p className="text-xs md:text-sm text-gray-600">{t('orders.details.orderTotalDescription')}</p>
            </div>
            <span className="text-xl md:text-2xl font-bold text-green-600">
              {formatCurrency(order.total_amount)}
            </span>
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-2 md:gap-3">
            {order.status === 'pending' && (
              <button
                onClick={() => onStatusUpdate(order.id, 'in_progress')}
                className="flex-1 bg-yellow-600 text-white py-2.5 md:py-3 rounded-xl font-semibold hover:bg-yellow-700 transition-all duration-300 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 flex items-center justify-center gap-2 text-sm md:text-base"
                disabled={isUpdatingStatus}
              >
                <RefreshCw className="h-4 w-4 md:h-5 md:w-5" />
                {isUpdatingStatus ? t('common.loading') : t('orders.actions.startPreparing')}
              </button>
            )}

            {order.status === 'in_progress' && (
              <button
                onClick={() => onStatusUpdate(order.id, 'completed')}
                className="flex-1 bg-green-600 text-white py-2.5 md:py-3 rounded-xl font-semibold hover:bg-green-700 transition-all duration-300 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 flex items-center justify-center gap-2 text-sm md:text-base"
                disabled={isUpdatingStatus}
              >
                <CheckCircle className="h-4 w-4 md:h-5 md:w-5" />
                {isUpdatingStatus ? t('common.loading') : t('orders.actions.markCompleted')}
              </button>
            )}

            {order.payment_status === 'unpaid' && order.status !== 'cancelled' && (
              <button
                onClick={() => onPaymentUpdate(order.id, 'paid', 'cash')}
                className="flex-1 bg-green-600 text-white py-2.5 md:py-3 rounded-xl font-semibold hover:bg-green-700 transition-all duration-300 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 flex items-center justify-center gap-2 text-sm md:text-base"
                disabled={isUpdatingPayment}
              >
                <CreditCard className="h-4 w-4 md:h-5 md:w-5" />
                {isUpdatingPayment ? t('common.loading') : t('orders.actions.markPaid')}
              </button>
            )}

            <button
              onClick={onClose}
              className="py-2.5 md:py-3 px-4 md:px-6 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-all duration-300 border border-gray-200 text-sm md:text-base"
            >
              {t('common.close')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StaffOrderManagement;